/**
 * Environment Configuration
 * 환경별 설정을 관리하는 유틸리티
 */

export interface AppConfig {
  title: string
  env: 'development' | 'production' | 'local'
  apiBaseUrl: string
  apiTimeout: number
  debug: boolean
  logLevel: 'debug' | 'info' | 'warn' | 'error'
}

/**
 * 현재 환경 설정을 가져옵니다
 */
export function getAppConfig(): AppConfig {
  return {
    title: import.meta.env.VITE_APP_TITLE || 'Lettaga',
    env: (import.meta.env.VITE_APP_ENV as AppConfig['env']) || 'development',
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001',
    apiTimeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '300000', 10), // 5분 (300초) 타임아웃
    debug: import.meta.env.VITE_APP_DEBUG === 'true',
    logLevel: (import.meta.env.VITE_APP_LOG_LEVEL as AppConfig['logLevel']) || 'info'
  }
}

/**
 * 현재 환경이 개발 환경인지 확인합니다
 */
export function isDevelopment(): boolean {
  return getAppConfig().env === 'development'
}

/**
 * 현재 환경이 프로덕션 환경인지 확인합니다
 */
export function isProduction(): boolean {
  return getAppConfig().env === 'production'
}

/**
 * 현재 환경이 로컬 환경인지 확인합니다
 */
export function isLocal(): boolean {
  return getAppConfig().env === 'local'
}

/**
 * 디버그 모드인지 확인합니다
 */
export function isDebugMode(): boolean {
  return getAppConfig().debug
}

/**
 * 환경별 로그 레벨에 따라 로그를 출력합니다
 */
export function log(level: 'debug' | 'info' | 'warn' | 'error', message: string, ...args: any[]): void {
  const config = getAppConfig()
  const logLevels = { debug: 0, info: 1, warn: 2, error: 3 }
  const currentLevel = logLevels[config.logLevel]
  const messageLevel = logLevels[level]

  if (messageLevel >= currentLevel) {
    const timestamp = new Date().toISOString()
    const prefix = `[${timestamp}] [${level.toUpperCase()}] [${config.env.toUpperCase()}]`
    
    switch (level) {
      case 'debug':
        console.debug(prefix, message, ...args)
        break
      case 'info':
        console.info(prefix, message, ...args)
        break
      case 'warn':
        console.warn(prefix, message, ...args)
        break
      case 'error':
        console.error(prefix, message, ...args)
        break
    }
  }
}

// 환경 설정 인스턴스
export const appConfig = getAppConfig()

// 편의 함수들
export const isDev = isDevelopment
export const isProd = isProduction
export const isLocalEnv = isLocal
export const isDebug = isDebugMode
